import React from 'react';
import { motion } from 'framer-motion';
import { Leaf, Recycle, Wind, Droplet, Sun, TreePine } from 'lucide-react';

const SustainableFashion = () => {
  const collections = [
    {
      name: "Eco Essentials",
      image: "https://images.unsplash.com/photo-1523381294911-8d3cead13475?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      description: "Everyday basics made from organic and recycled materials."
    },
    {
      name: "Zero Waste Collection",
      image: "https://images.unsplash.com/photo-1581655353564-df123a1eb820?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      description: "Innovative designs that minimize fabric waste."
    },
    {
      name: "Upcycled Elegance",
      image: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      description: "Unique pieces created from reclaimed materials."
    },
    {
      name: "Bio-Based Beauty",
      image: "https://images.unsplash.com/photo-1523381294911-8d3cead13475?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      description: "Fashion made from innovative plant-based materials."
    }
  ];

  const materials = [
    {
      name: "Organic Cotton",
      icon: <Leaf className="h-8 w-8 text-emerald-400" />,
      description: "Grown without harmful pesticides or synthetic fertilizers."
    },
    {
      name: "Recycled Polyester",
      icon: <Recycle className="h-8 w-8 text-emerald-400" />,
      description: "Made from post-consumer plastic bottles."
    },
    {
      name: "Hemp Fabric",
      icon: <Wind className="h-8 w-8 text-emerald-400" />,
      description: "Durable and naturally antimicrobial."
    },
    {
      name: "Bamboo Textile",
      icon: <TreePine className="h-8 w-8 text-emerald-400" />,
      description: "Renewable resource with minimal environmental impact."
    }
  ];

  const impacts = [
    {
      title: "Water Conservation",
      icon: <Droplet className="h-12 w-12 text-emerald-400" />,
      stats: "70% Less",
      description: "Water usage compared to conventional fashion production."
    },
    {
      title: "Carbon Reduction",
      icon: <Wind className="h-12 w-12 text-emerald-400" />,
      stats: "50% Lower",
      description: "Carbon footprint through sustainable practices."
    },
    {
      title: "Solar Powered",
      icon: <Sun className="h-12 w-12 text-emerald-400" />,
      stats: "100%",
      description: "Production facilities powered by renewable energy."
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-20"
    >
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1581655353564-df123a1eb820?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">Sustainable Fashion</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Style that respects our planet and its people
            </p>
          </motion.div>
        </div>
      </section>

      {/* Collections */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Eco-Conscious Collections</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Fashion that makes a difference without compromising on style
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {collections.map((collection, index) => (
              <motion.div
                key={collection.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-2xl"
              >
                <div className="aspect-w-16 aspect-h-9">
                  <img
                    src={collection.image}
                    alt={collection.name}
                    className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-6">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">{collection.name}</h3>
                    <p className="text-gray-300">{collection.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="py-24 bg-black/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Sustainable Materials</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Carefully selected eco-friendly materials that make a difference
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {materials.map((material, index) => (
              <motion.div
                key={material.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 rounded-2xl border border-gray-800 hover:border-emerald-400/50 transition-colors"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-400/10 mb-6">
                  {material.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{material.name}</h3>
                <p className="text-gray-400">{material.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Environmental Impact */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Impact</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Making a measurable difference in the fashion industry
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {impacts.map((impact, index) => (
              <motion.div
                key={impact.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8 rounded-2xl border border-gray-800 hover:border-emerald-400/50 transition-colors"
              >
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-400/10 mb-6">
                  {impact.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{impact.title}</h3>
                <p className="text-3xl font-bold text-emerald-400 mb-2">{impact.stats}</p>
                <p className="text-gray-400">{impact.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-24 bg-black/95">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Leaf className="h-16 w-16 text-emerald-400 mx-auto mb-8" />
          <h2 className="text-4xl font-bold mb-6">Our Commitment to Sustainability</h2>
          <p className="text-gray-400 mb-8">
            Join us in our mission to create a more sustainable future for fashion. Every piece in our collection is crafted with care for the environment and respect for our artisans.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-3 bg-emerald-400 text-black font-semibold rounded-full hover:bg-emerald-300 transition-colors"
          >
            Learn More About Our Process
          </motion.button>
        </div>
      </section>
    </motion.div>
  );
};

export default SustainableFashion; 